import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import { getServiceZone, ServiceZone } from "@/lib/service-zones";
import { loadGoogleMapsLibrary } from "@/lib/google-maps-loader";

interface PlaceDetails {
  addressLine1: string;
  city: string;
  postalCode: string;
  lat: number;
  lng: number;
  serviceZone: ServiceZone;
}

interface Suggestion {
  placePrediction: any;
  text: string;
  secondaryText: string;
}

export function useGooglePlacesAutocomplete(
  inputRef: React.RefObject<HTMLInputElement>,
  onPlaceSelected: (details: PlaceDetails) => void,
  options?: {
    cityRef?: React.RefObject<HTMLInputElement>;
    postalCodeRef?: React.RefObject<HTMLInputElement>;
  }
) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const sessionTokenRef = useRef<any>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isUpdatingProgrammaticallyRef = useRef(false);
  const suggestionsStateRef = useRef(suggestions);
  const showSuggestionsStateRef = useRef(showSuggestions);
  const selectedIndexStateRef = useRef(selectedIndex);
  const handleSuggestionSelectRef = useRef<((suggestion: Suggestion) => Promise<void>) | null>(null);

  // Update refs when state changes
  useEffect(() => {
    suggestionsStateRef.current = suggestions;
  }, [suggestions]);

  useEffect(() => {
    showSuggestionsStateRef.current = showSuggestions;
  }, [showSuggestions]);

  useEffect(() => {
    selectedIndexStateRef.current = selectedIndex;
  }, [selectedIndex]);

  // Helper function to check and log available APIs
  const checkAvailableAPIs = useCallback(() => {
    const places = window.google?.maps?.places;
    if (!places) {
      console.error("🔴 Google Maps Places library not loaded");
      console.error("window.google:", window.google);
      console.error("window.google.maps:", window.google?.maps);
      return;
    }
    console.log("✅ Google Maps Places library loaded");
    console.log("📋 Available Places APIs:", Object.keys(places));
    console.log("🔍 AutocompleteSuggestion available:", !!places.AutocompleteSuggestion);
    console.log("🔍 Autocomplete available:", !!places.Autocomplete);
    console.log("🔍 AutocompleteSessionToken available:", !!places.AutocompleteSessionToken);
    
    // Log detailed information about available classes
    if (places.AutocompleteSuggestion) {
      console.log("✅ AutocompleteSuggestion class found:", places.AutocompleteSuggestion);
    } else {
      console.warn("⚠️ AutocompleteSuggestion class NOT found");
    }
    
    if (places.Autocomplete) {
      console.log("✅ Autocomplete class found:", places.Autocomplete);
    } else {
      console.warn("⚠️ Autocomplete class NOT found");
    }
  }, []);

  // Load Google Maps library
  useEffect(() => {
    const loadGoogleMaps = async () => {
      try {
        console.log("🔄 Starting to load Google Maps Places library...");
        await loadGoogleMapsLibrary("places");
        console.log("✅ Google Maps Places library loaded successfully");
        
        // Wait a bit for places to be fully available
        // Sometimes there's a small delay between library load and API availability
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Check what APIs are available
        if (window.google?.maps?.places) {
          checkAvailableAPIs();
        } else {
          console.warn("⚠️ Places library loaded but window.google.maps.places not yet available");
          // Retry after a short delay
          setTimeout(() => {
            if (window.google?.maps?.places) {
              checkAvailableAPIs();
            } else {
              console.error("❌ Places library still not available after retry");
            }
          }, 500);
        }
        
        setIsLoaded(true);
        console.log("✅ Google Maps Autocomplete hook initialized");
      } catch (error) {
        console.error("❌ Error loading Google Maps:", error);
        console.error("Error details:", {
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        });
        toast.error("Failed to load address autocomplete");
      }
    };

    loadGoogleMaps();
  }, [checkAvailableAPIs]);

  // Fetch suggestions from Google Places API
  const fetchSuggestions = useCallback(async (input: string) => {
    console.log("🔍 fetchSuggestions called with input:", input, "length:", input?.length);
    
    if (!input || input.length < 3) {
      console.log("⏭️ Skipping fetchSuggestions - input too short or empty");
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      console.log("🔍 Fetching suggestions for input:", input);
      
      // Wait for places library to be loaded
      if (!window.google?.maps?.places) {
        console.log("⏳ Places library not loaded, loading now...");
        await loadGoogleMapsLibrary("places");
        console.log("✅ Places library loaded");
      }

      const places = window.google?.maps?.places as any;
      
      // Log detailed information about what's available
      console.log("📋 Checking available Places APIs...");
      console.log("Places object:", places);
      console.log("Available properties:", places ? Object.keys(places) : "places is undefined");
      
      // Check if AutocompleteSuggestion is available
      const AutocompleteSuggestion = places?.AutocompleteSuggestion;
      
      if (!AutocompleteSuggestion) {
        console.warn("⚠️ AutocompleteSuggestion not available");
        console.warn("Available Places APIs:", places ? Object.keys(places) : "places is undefined");
        console.warn("Trying to find alternative APIs...");
        
        // Check for traditional Autocomplete as fallback
        if (places?.Autocomplete) {
          console.warn("ℹ️ Traditional Autocomplete API is available but not being used");
        }
        
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      
      console.log("✅ AutocompleteSuggestion found and available");

      // Create session token if needed
      const AutocompleteSessionToken = places?.AutocompleteSessionToken;
      if (!sessionTokenRef.current && AutocompleteSessionToken) {
        console.log("🔑 Creating new AutocompleteSessionToken");
        sessionTokenRef.current = new AutocompleteSessionToken();
      } else if (!AutocompleteSessionToken) {
        console.warn("⚠️ AutocompleteSessionToken not available");
      }

      const request: any = {
        input,
        sessionToken: sessionTokenRef.current,
      };

      console.log("📤 Sending request to AutocompleteSuggestion.fetchAutocompleteSuggestions:", {
        input,
        hasSessionToken: !!sessionTokenRef.current,
      });

      const result = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      const apiSuggestions = result?.suggestions || [];
      
      console.log("📥 Received response:", {
        suggestionsCount: apiSuggestions.length,
        result: result,
      });

      const formattedSuggestions: Suggestion[] = apiSuggestions.map((suggestion: any) => {
        const prediction = suggestion.placePrediction;
        return {
          placePrediction: prediction,
          text: prediction.text?.text || prediction.text || "",
          secondaryText: prediction.text?.secondaryText || "",
        };
      });

      setSuggestions(formattedSuggestions);
      setShowSuggestions(formattedSuggestions.length > 0);
      setSelectedIndex(-1);
      
      console.log("✅ Successfully formatted suggestions:", formattedSuggestions.length);
    } catch (error) {
      console.error("❌ Error fetching suggestions:", error);
      console.error("Error details:", {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        name: error instanceof Error ? error.name : undefined,
      });
      
      // Check if error is related to API key, CSP, or API availability
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes("API key") || errorMessage.includes("key")) {
        console.error("🔑 Possible API key issue");
      }
      if (errorMessage.includes("CSP") || errorMessage.includes("Content Security Policy")) {
        console.error("🛡️ Possible CSP (Content Security Policy) issue");
      }
      if (errorMessage.includes("not available") || errorMessage.includes("undefined")) {
        console.error("⚠️ API may not be available");
      }
      
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, []);

  // Handle suggestion selection
  const handleSuggestionSelect = useCallback(async (suggestion: Suggestion) => {
    try {
      const place = suggestion.placePrediction.toPlace();

      // Fetch the required fields
      await place.fetchFields({
        fields: [
          "displayName",
          "formattedAddress",
          "addressComponents",
          "location",
        ],
      });

      if (!place.location || !place.addressComponents) {
        toast.error("Unable to get address details");
        return;
      }

      // Extract address components
      let addressLine1 = "";
      let city = "";
      let postalCode = "";

      place.addressComponents.forEach((component: any) => {
        const types = component.types || [];
        // Check for minified properties (Dg, Eg, etc.) that Google Maps uses internally
        const longName = component.longText || component.longName || component.Dg || component.text || "";
        const shortName = component.shortText || component.shortName || component.Eg || "";

        if (types.includes("street_number")) {
          addressLine1 = longName + " ";
        }
        if (types.includes("route")) {
          addressLine1 += longName;
        }
        if (types.includes("locality")) {
          city = longName;
        }
        if (types.includes("postal_code")) {
          postalCode = longName || shortName || component.text || "";
        }
      });

      // Get location coordinates - handle both function and direct value cases
      let lat: number;
      let lng: number;
      
      if (typeof place.location.lat === 'function') {
        lat = place.location.lat();
        lng = place.location.lng();
      } else {
        lat = place.location.lat;
        lng = place.location.lng;
      }
      
      const serviceZone = getServiceZone(lat, lng);

      // Update input value and trigger React onChange
      if (inputRef.current) {
        isUpdatingProgrammaticallyRef.current = true;
        const inputValue = place.formattedAddress || addressLine1.trim();
        inputRef.current.value = inputValue;
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          "value"
        )?.set;
        nativeInputValueSetter?.call(inputRef.current, inputValue);
        const event = new Event("input", { bubbles: true });
        inputRef.current.dispatchEvent(event);
        isUpdatingProgrammaticallyRef.current = false;
      }

      // Auto-fill city field if ref is provided
      if (options?.cityRef?.current && city) {
        const cityInput = options.cityRef.current;
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          "value"
        )?.set;
        nativeInputValueSetter?.call(cityInput, city);
        const cityEvent = new Event("input", { bubbles: true });
        cityInput.dispatchEvent(cityEvent);
      }

      const details: PlaceDetails = {
        addressLine1: addressLine1.trim(),
        city,
        postalCode,
        lat,
        lng,
        serviceZone,
      };

      setShowSuggestions(false);
      setSuggestions([]);
      
      // Call the callback which will update formData in Step1 (including postal code formatting)
      onPlaceSelected(details);
      
      // Also directly update postal code input as fallback to ensure it shows
      if (options?.postalCodeRef?.current && postalCode) {
        const postalInput = options.postalCodeRef.current;
        
        // Format postal code: remove spaces, uppercase, limit to 6 chars, format as M5A 1N4
        const cleaned = postalCode.replace(/\s/g, '').toUpperCase();
        const lettersAndNumbers = cleaned.replace(/[^A-Z0-9]/g, '');
        const limited = lettersAndNumbers.slice(0, 6);
        let formatted = '';
        
        if (limited.length === 0) {
          formatted = '';
        } else if (limited.length <= 3) {
          formatted = limited;
        } else {
          formatted = `${limited.slice(0, 3)} ${limited.slice(3)}`;
        }
        
        // Set the value directly
        postalInput.value = formatted;
        
        // Trigger input event to update React state
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          "value"
        )?.set;
        
        if (nativeInputValueSetter) {
          nativeInputValueSetter.call(postalInput, formatted);
          const inputEvent = new Event("input", { bubbles: true, cancelable: true });
          postalInput.dispatchEvent(inputEvent);
        } else {
          postalInput.value = formatted;
          const inputEvent = new Event("input", { bubbles: true });
          postalInput.dispatchEvent(inputEvent);
        }
        
        // Trigger change event as well
        const changeEvent = new Event("change", { bubbles: true, cancelable: true });
        postalInput.dispatchEvent(changeEvent);
      }

      // Consume session token after selection
      if (sessionTokenRef.current) {
        sessionTokenRef.current = null;
      }
    } catch (error) {
      console.error("❌ Error processing place selection:", error);
      console.error("Error details:", {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        name: error instanceof Error ? error.name : undefined,
      });
      toast.error("Failed to process address selection");
    }
  }, [inputRef, onPlaceSelected, options]);

  // Update handleSuggestionSelect ref when it changes
  useEffect(() => {
    handleSuggestionSelectRef.current = handleSuggestionSelect;
  }, [handleSuggestionSelect]);

  // Handle input changes with debounce
  useEffect(() => {
    console.log("🔄 Setting up input event listeners, inputRef.current:", !!inputRef.current, "isLoaded:", isLoaded);
    
    if (!inputRef.current || !isLoaded) {
      console.log("⏭️ Skipping event listener setup - inputRef:", !!inputRef.current, "isLoaded:", isLoaded);
      return;
    }

    const inputElement = inputRef.current;
    console.log("✅ Adding event listeners to input element:", inputElement);

    const handleInput = (e: Event) => {
      // Ignore events triggered programmatically
      if (isUpdatingProgrammaticallyRef.current) {
        console.log("⏭️ Ignoring programmatically triggered input event");
        return;
      }

      const target = e.target as HTMLInputElement;
      const value = target.value;

      console.log("⌨️ Input event detected, value:", value, "length:", value.length);

      // Clear previous debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Debounce API calls
      console.log("⏱️ Setting debounce timer for value:", value);
      debounceTimerRef.current = setTimeout(() => {
        console.log("⏰ Debounce timer fired, calling fetchSuggestions with:", value);
        fetchSuggestions(value);
      }, 100);
      console.log("✅ Debounce timer set, timer ID:", debounceTimerRef.current);
    };

    const handleFocus = () => {
      if (suggestionsStateRef.current.length > 0) {
        setShowSuggestions(true);
      }
    };

    const handleBlur = (e: FocusEvent) => {
      // Delay to allow click on suggestions
      const relatedTarget = e.relatedTarget as HTMLElement;
      if (suggestionsRef.current?.contains(relatedTarget)) {
        return;
      }
      
      setTimeout(() => {
        const activeElement = document.activeElement;
        if (
          activeElement !== inputElement &&
          !suggestionsRef.current?.contains(activeElement)
        ) {
          setShowSuggestions(false);
        }
      }, 250);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      const currentSuggestions = suggestionsStateRef.current;
      const currentShowSuggestions = showSuggestionsStateRef.current;
      const currentSelectedIndex = selectedIndexStateRef.current;
      
      if (!currentShowSuggestions || currentSuggestions.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev < currentSuggestions.length - 1 ? prev + 1 : prev));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
      } else if (e.key === "Enter" && currentSelectedIndex >= 0) {
        e.preventDefault();
        if (handleSuggestionSelectRef.current) {
          handleSuggestionSelectRef.current(currentSuggestions[currentSelectedIndex]);
        }
      } else if (e.key === "Escape") {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    inputElement.addEventListener("input", handleInput);
    inputElement.addEventListener("focus", handleFocus);
    inputElement.addEventListener("blur", handleBlur);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      inputElement.removeEventListener("input", handleInput);
      inputElement.removeEventListener("focus", handleFocus);
      inputElement.removeEventListener("blur", handleBlur);
      window.removeEventListener("keydown", handleKeyDown);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [inputRef, isLoaded, fetchSuggestions]);

  // Render suggestions dropdown
  useEffect(() => {
    if (!inputRef.current || !showSuggestions || suggestions.length === 0) {
      // Remove suggestions dropdown if it exists
      if (suggestionsRef.current?.parentElement) {
        suggestionsRef.current.parentElement.removeChild(suggestionsRef.current);
      }
      return;
    }

    const inputElement = inputRef.current;
    const inputContainer = inputElement.parentElement;

    if (!inputContainer) return;

    // Create or get suggestions container
    let container = suggestionsRef.current;
    if (!container) {
      container = document.createElement("div");
      container.className =
        "absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-auto";
      container.style.top = `${inputElement.offsetHeight + 4}px`;
      container.style.left = "0";
      suggestionsRef.current = container;
    }

    // Clear previous content
    container.innerHTML = "";

    // Create suggestion items
    suggestions.forEach((suggestion, index) => {
      const item = document.createElement("div");
      item.className = `px-4 py-2 cursor-pointer hover:bg-accent hover:text-accent-foreground transition-colors ${
        index === selectedIndex ? "bg-accent text-accent-foreground" : ""
      }`;
      
      const textDiv = document.createElement("div");
      textDiv.className = "font-medium text-sm";
      textDiv.textContent = suggestion.text;
      
      item.appendChild(textDiv);
      
      if (suggestion.secondaryText) {
        const secondaryDiv = document.createElement("div");
        secondaryDiv.className = "text-xs text-muted-foreground mt-0.5";
        secondaryDiv.textContent = suggestion.secondaryText;
        item.appendChild(secondaryDiv);
      }
      
      // Use mousedown instead of click to fire before blur event
      const selectHandler = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (handleSuggestionSelectRef.current) {
          handleSuggestionSelectRef.current(suggestion);
        }
      };
      
      item.addEventListener("mousedown", selectHandler);
      item.addEventListener("mouseenter", () => setSelectedIndex(index));
      container.appendChild(item);
    });

    // Position and append container
    if (!inputContainer.contains(container)) {
      if (getComputedStyle(inputContainer).position === "static") {
        inputContainer.style.position = "relative";
      }
      inputContainer.appendChild(container);
    }

    // Cleanup
    return () => {
      if (suggestionsRef.current?.parentElement) {
        suggestionsRef.current.parentElement.removeChild(suggestionsRef.current);
      }
    };
  }, [suggestions, showSuggestions, selectedIndex, inputRef, handleSuggestionSelect]);

  return { isLoaded };
}
